import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Search, Edit2, Trash2, User, Mail, Loader2,
  AlertCircle, CheckCircle, X, ShieldCheck, Phone,
} from "lucide-react";
import { teacherAccountsAPI } from "../../api/teacherAccounts";

/* ─── Toast ─────────────────────────────────────────────────────── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div style={{ ...styles.toast, ...(type === "error" ? styles.toastError : styles.toastSuccess) }}>
      {type === "success" ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
      {message}
      <button onClick={onClose} style={styles.toastClose}><X size={15} /></button>
    </div>
  );
}

/* ─── Create/Edit Modal ─────────────────────────────────────────── */
function TeacherAccountModal({ teacher, onClose, onSaved }) {
  const isEdit = !!teacher;
  const [form, setForm] = useState({
    email: teacher?.email || "",
    full_name: teacher?.full_name || "",
    password: "",
    new_password: "",
    phone_number: teacher?.phone_number || "",
    specialization: teacher?.specialization || "",
    bio: teacher?.bio || "",
    is_active: teacher?.is_active ?? true,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!isEdit) {
      if (!form.email.trim()) errs.email = "البريد الإلكتروني مطلوب";
      if (!form.full_name.trim()) errs.full_name = "الاسم مطلوب";
      if (!form.password.trim()) errs.password = "كلمة المرور مطلوبة";
    } else {
      if (!form.full_name.trim()) errs.full_name = "الاسم مطلوب";
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) return setErrors(errs);

    setLoading(true);
    try {
      if (isEdit) {
        const payload = {
          full_name: form.full_name,
          phone_number: form.phone_number,
          specialization: form.specialization,
          bio: form.bio,
          is_active: form.is_active,
        };
        if (form.new_password.trim()) payload.new_password = form.new_password;
        await teacherAccountsAPI.update(teacher.id, payload);
        onSaved("✅ تم تحديث بيانات المدرس بنجاح");
      } else {
        await teacherAccountsAPI.create({
          email: form.email,
          full_name: form.full_name,
          password: form.password,
          phone_number: form.phone_number,
          specialization: form.specialization,
          bio: form.bio,
        });
        onSaved("✅ تم إنشاء حساب المدرس بنجاح");
      }
    } catch (err) {
      const data = err?.response?.data;
      if (data && typeof data === "object") setErrors(data);
      else setErrors({ general: "حدث خطأ، حاول مرة أخرى" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{isEdit ? "تعديل حساب المدرس" : "إضافة حساب مدرس جديد"}</h2>
          <button onClick={onClose} style={styles.iconBtn}><X size={15} /></button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form} dir="rtl">
          {errors.general && <div style={styles.errorBanner}><AlertCircle size={15} />{errors.general}</div>}

          {!isEdit && (
            <>
              <Field label="البريد الإلكتروني" required error={errors.email}>
                <input type="email" name="email" value={form.email} onChange={handleChange}
                  dir="ltr" placeholder="teacher@example.com" style={styles.input} />
              </Field>
              <Field label="كلمة المرور" required error={errors.password}>
                <input type="password" name="password" value={form.password} onChange={handleChange}
                  style={styles.input} />
              </Field>
            </>
          )}

          <Field label="الاسم الكامل" required error={errors.full_name}>
            <input type="text" name="full_name" value={form.full_name} onChange={handleChange} style={styles.input} />
          </Field>

          <Field label="رقم الهاتف">
            <input type="text" name="phone_number" value={form.phone_number} onChange={handleChange} style={styles.input} />
          </Field>

          <Field label="التخصص">
            <input type="text" name="specialization" value={form.specialization} onChange={handleChange} style={styles.input} />
          </Field>

          <Field label="نبذة">
            <textarea name="bio" value={form.bio} onChange={handleChange} rows={3}
              style={{ ...styles.input, resize: "none" }} />
          </Field>

          {isEdit && (
            <>
              <Field label="كلمة مرور جديدة (اختياري)">
                <input type="password" name="new_password" value={form.new_password} onChange={handleChange} style={styles.input} />
              </Field>
              <label style={styles.checkboxRow}>
                <input type="checkbox" checked={form.is_active}
                  onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} />
                <span>الحساب نشط (يقدر يسجل دخول)</span>
              </label>
            </>
          )}

          <div style={{ display: "flex", gap: 10, paddingTop: 6 }}>
            <button type="submit" disabled={loading} style={styles.primaryBtn}>
              {loading && <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />}
              {isEdit ? "حفظ التعديلات" : "إنشاء الحساب"}
            </button>
            <button type="button" onClick={onClose} style={styles.ghostBtn}>إلغاء</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <div style={styles.label}>{label} {required && <span style={{ color: "var(--danger)" }}>*</span>}</div>
      {children}
      {error && <p style={styles.fieldError}>{error}</p>}
    </div>
  );
}

/* ─── Delete Modal ──────────────────────────────────────────────── */
function DeleteModal({ teacher, onClose, onDeleted }) {
  const [loading, setLoading] = useState(false);
  const handleDelete = async () => {
    setLoading(true);
    try {
      await teacherAccountsAPI.remove(teacher.id);
      onDeleted("🗑️ تم حذف حساب المدرس بنجاح");
    } catch {
      onClose();
    } finally {
      setLoading(false);
    }
  };
  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.modal, maxWidth: 380 }}>
        <div style={{ padding: "32px 28px", textAlign: "center" }} dir="rtl">
          <div style={styles.deleteIconWrap}><Trash2 size={28} color="var(--danger)" /></div>
          <h3 style={styles.modalTitle}>حذف حساب المدرس</h3>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.7, margin: "8px 0 24px" }}>
            هل أنت متأكد من حذف حساب <strong style={{ color: "var(--text-primary)" }}>{teacher.full_name}</strong>؟
            هيتشال معاه كل صلاحياته. لا يمكن التراجع.
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={handleDelete} disabled={loading} style={styles.dangerSolidBtn}>
              {loading && <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />}
              نعم، احذف
            </button>
            <button onClick={onClose} style={styles.ghostBtn}>إلغاء</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────── */
export default function TeacherAccountsList() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);

  const fetchTeachers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await teacherAccountsAPI.list();
      setTeachers(res.data || []);
    } catch {
      setToast({ message: "فشل تحميل حسابات المدرسين", type: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTeachers(); }, [fetchTeachers]);

  const open = (type, t = null) => { setSelected(t); setModal(type); };
  const close = () => { setModal(null); setSelected(null); };
  const handleSaved = (msg) => { close(); setToast({ message: msg, type: "success" }); fetchTeachers(); };
  const handleDeleted = (msg) => { close(); setToast({ message: msg, type: "success" }); fetchTeachers(); };

  const filtered = teachers.filter((t) =>
    t.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    t.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }} dir="rtl">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      {(modal === "add" || modal === "edit") && (
        <TeacherAccountModal teacher={modal === "edit" ? selected : null} onClose={close} onSaved={handleSaved} />
      )}
      {modal === "delete" && <DeleteModal teacher={selected} onClose={close} onDeleted={handleDeleted} />}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "var(--text-primary)" }}>حسابات المدرسين</h1>
          <p style={{ fontSize: 12.5, color: "var(--text-secondary)", marginTop: 2 }}>
            {teachers.length} حساب مدرس · إدارة تسجيل الدخول والصلاحيات
          </p>
        </div>
        <button onClick={() => open("add")} style={styles.primaryBtn}>
          <Plus size={15} /> إضافة حساب مدرس
        </button>
      </div>

      <div style={{ position: "relative", marginBottom: 20, maxWidth: 340 }}>
        <Search size={15} color="var(--text-secondary)" style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)" }} />
        <input
          type="text" placeholder="ابحث بالاسم أو الإيميل..." value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...styles.input, padding: "10px 44px 10px 16px" }}
        />
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <Loader2 size={32} style={{ animation: "spin 1s linear infinite", color: "var(--accent)" }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={styles.empty}>
          <User size={48} style={{ opacity: 0.3, margin: "0 auto 14px", color: "var(--text-secondary)" }} />
          <p style={{ color: "var(--text-primary)", fontWeight: 700 }}>مفيش حسابات مدرسين لسه</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 16 }}>
          {filtered.map((t) => (
            <div key={t.id} style={styles.card}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={styles.avatar}><User size={22} color="var(--accent)" /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text-primary)" }}>{t.full_name}</span>
                    <span style={{ ...styles.badge, ...(t.is_active ? styles.badgeOn : styles.badgeOff) }}>
                      {t.is_active ? "نشط" : "غير نشط"}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, color: "var(--text-secondary)" }}>
                    <Mail size={11} /><span style={{ fontSize: 11.5, direction: "ltr" }}>{t.email}</span>
                  </div>
                  {t.phone_number && (
                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3, color: "var(--text-secondary)" }}>
                      <Phone size={11} /><span style={{ fontSize: 11.5 }}>{t.phone_number}</span>
                    </div>
                  )}
                  {t.specialization && (
                    <div style={{ fontSize: 11.5, color: "var(--accent)", marginTop: 4, fontWeight: 600 }}>{t.specialization}</div>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <Link to={`/dashboard/teachers/${t.id}/permissions`} style={{ ...styles.smallBtn, ...styles.smallBtnAccent, textDecoration: "none" }}>
                  <ShieldCheck size={13} /> الصلاحيات
                </Link>
                <button onClick={() => open("edit", t)} style={styles.smallBtn}>
                  <Edit2 size={13} /> تعديل
                </button>
                <button onClick={() => open("delete", t)} style={{ ...styles.smallBtn, ...styles.smallBtnDanger }}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const styles = {
  overlay: { position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  modal: { background: "var(--dropdown-bg)", backdropFilter: "blur(28px)", border: "1px solid var(--glass-border)", borderRadius: 20, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: "1px solid var(--glass-border)" },
  modalTitle: { fontSize: 16, fontWeight: 800, color: "var(--text-primary)" },
  form: { padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 },
  label: { fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", marginBottom: 5 },
  input: { width: "100%", padding: "9px 12px", borderRadius: 10, border: "1px solid var(--glass-border)", background: "var(--glass-bg)", color: "var(--text-primary)", fontSize: 13, outline: "none" },
  fieldError: { fontSize: 11, color: "var(--danger)", marginTop: 4 },
  errorBanner: { display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "var(--danger-bg)", border: "1px solid var(--danger)", borderRadius: 10, color: "var(--danger)", fontSize: 13 },
  checkboxRow: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-primary)", cursor: "pointer" },
  primaryBtn: { display: "inline-flex", alignItems: "center", gap: 8, justifyContent: "center", background: "linear-gradient(135deg, var(--accent), var(--accent-2))", color: "#fff", border: "none", borderRadius: 12, padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", flex: 1 },
  ghostBtn: { background: "var(--glass-bg)", border: "1px solid var(--glass-border)", borderRadius: 12, color: "var(--text-primary)", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "10px 18px", flex: 1 },
  dangerSolidBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 6, flex: 1, padding: "12px 0", background: "var(--danger)", border: "none", borderRadius: 12, color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" },
  deleteIconWrap: { width: 64, height: 64, borderRadius: 18, background: "var(--danger-bg)", border: "1px solid var(--danger)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" },
  iconBtn: { width: 32, height: 32, borderRadius: 8, background: "var(--glass-bg)", border: "1px solid var(--glass-border)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--text-secondary)" },
  toast: { position: "fixed", top: 20, right: 20, zIndex: 999, display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", borderRadius: 16, fontSize: 13.5, fontWeight: 600, color: "#fff" },
  toastSuccess: { background: "linear-gradient(135deg,#10b981,#059669)" },
  toastError: { background: "linear-gradient(135deg,#ef4444,#dc2626)" },
  toastClose: { background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.7)", display: "flex" },
  empty: { textAlign: "center", padding: "60px 24px", background: "var(--glass-bg)", border: "1.5px dashed var(--glass-border)", borderRadius: 20 },
  card: { background: "var(--glass-bg)", border: "1px solid var(--glass-border)", borderRadius: 16, padding: 16, backdropFilter: "blur(16px)" },
  avatar: { width: 46, height: 46, borderRadius: 14, background: "linear-gradient(135deg, rgba(79,124,255,0.15), rgba(139,92,246,0.15))", border: "1.5px solid rgba(79,124,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  badge: { fontSize: 10.5, fontWeight: 700, padding: "3px 9px", borderRadius: 8, flexShrink: 0 },
  badgeOn: { background: "rgba(52,211,153,0.15)", color: "#10b981" },
  badgeOff: { background: "var(--glass-bg)", color: "var(--text-secondary)", border: "1px solid var(--glass-border)" },
  smallBtn: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "8px 0", borderRadius: 10, border: "1px solid var(--glass-border)", background: "var(--glass-bg)", color: "var(--text-primary)", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  smallBtnAccent: { color: "var(--accent)" },
  smallBtnDanger: { color: "var(--danger)", flex: "0 0 auto", padding: "8px 12px" },
};